import { notify, confirm } from '../../utils/notify';
import React, { useState, useEffect } from 'react';
import Header from '../Header';
import { vendorAPI, uploadFileToS3 } from '../../services/api';
import { useListControls, ListToolbar, Pagination } from '../../components/ListControls';
import Topbar from "../../components/Topbar";

const money = n => new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(Number(n)||0);
const TERMS = [{code:'100PIA',label:'100% PIA'},{code:'NET15',label:'NET 15'},{code:'NET30',label:'NET 30'},{code:'NET45',label:'NET 45'},{code:'NET60',label:'NET 60'},{code:'ADV50',label:'50% PIA, 50% Net 30'},{code:'ADV',label:'Advance'},{code:'MILESTONE',label:'Milestone Based'},{code:'DOWNPAY',label:'Clear Down Payment'},{code:'RETENTION10',label:'Retention 10%'}];
const STATUS = { SUBMITTED:{cls:'status-inreview',icon:'bi-send',label:'Submitted'}, PENDING_L1:{cls:'status-pending',icon:'bi-hourglass',label:'Pending L1'}, PENDING_L2:{cls:'status-pending',icon:'bi-hourglass-split',label:'Pending L2'}, PENDING_L3:{cls:'status-inreview',icon:'bi-person-badge',label:'Pending L3'}, PENDING_FINANCE:{cls:'status-inreview',icon:'bi-bank',label:'Pending Finance'}, FINANCE_APPROVED:{cls:'status-approved',icon:'bi-check-circle',label:'Finance Approved'}, REJECTED:{cls:'status-rejected',icon:'bi-x-circle',label:'Rejected'}, PAYOUT_INITIATED:{cls:'status-inreview',icon:'bi-send-check',label:'Payout Initiated'}, PAID:{cls:'status-approved',icon:'bi-currency-rupee',label:'Paid'} };

function PaymentRequests() {
  const [requests, setRequests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [form, setForm] = useState({vendorId:'',projectId:'',poNumber:'',costCenter:'CC-001',paymentTerms:'NET30',materialDetails:'',requestedAmount:''});
  const [saving, setSaving] = useState(false);
  const [poFile, setPoFile] = useState(null);
  

  const load = () => {
    setLoading(true);
    Promise.all([vendorAPI.listPaymentRequests(), vendorAPI.listVendors({status:'ACTIVE'}), vendorAPI.listProjects({status:'ACTIVE'})])
      .then(([r,v,p])=>{ setRequests(r.requests||[]); setVendors(v.vendors||[]); setProjects(p.projects||[]); })
      .catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); },[]);

  
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleSubmit = async () => {
    if (!form.vendorId||!form.projectId||!form.requestedAmount) { notify.error('Vendor, Project and Amount are required'); return; }
    setSaving(true);
    try {
      const d = await vendorAPI.createPaymentRequest({...form, requestedAmount:Number(form.requestedAmount)});
      const requestId = d.request?._id;
      if (poFile && requestId) {
        try {
          const up = await vendorAPI.requestPoAttachmentUpload(requestId, { fileName: poFile.name, fileType: poFile.type || 'application/octet-stream' });
          await uploadFileToS3(up.uploadUrl, poFile);
        } catch (upErr) {
          notify.warn('Payment request created, but the PO attachment failed to upload. You can retry from the request details.');
        }
      }
      notify.success(`Payment request created! Code: ${d.request?.paymentRequestCode}`);
      setModal(false); setForm({vendorId:'',projectId:'',poNumber:'',costCenter:'CC-001',paymentTerms:'NET30',materialDetails:'',requestedAmount:''}); setPoFile(null); load();
    } catch(e) { notify.error(e.message||'Failed'); } finally { setSaving(false); }
  };

  const counts = {};
  ['SUBMITTED','PENDING_L1','PENDING_L2','PENDING_L3','PENDING_FINANCE','FINANCE_APPROVED','PAID','REJECTED'].forEach(s=>counts[s]=(requests.filter(r=>r.status===s).length));
  const filtered = filter==='ALL' ? requests : requests.filter(r=>r.status===filter);
  const lc = useListControls(filtered, {
    searchFields: [r=>r.vendorId?.name, 'paymentRequestCode', 'poNumber', r=>r.projectId?.projectCode],
    dateField: 'createdAt', pageSize: 10,
  });

  // Get cost centers for selected project
  const selectedProject = projects.find(p=>p._id===form.projectId);
  const costCenters = selectedProject?.costCenters||[{code:'CC-001',name:'Civil Works'},{code:'CC-002',name:'Mechanical'},{code:'CC-003',name:'Electrical'}];

  return (
    <>
      <Header/>
      <div id="db-wrapper"><main id="page-content">
          <Topbar section="Vendor Payment" /><section className="container-fluid p-4 pt-3">
        

        <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div><h5 className="text-white lh-1">Payment Requests</h5><div className="sub">Step 3 — Create GRN-based payment requests linked to vendors and projects</div></div>
          <button onClick={()=>setModal(true)} className="btn fw-semibold px-4 py-2" style={{background:'rgba(255,255,255,0.2)',color:'#fff',borderRadius:10,border:'1.5px solid rgba(255,255,255,0.35)'}}>
            <i className="bi bi-plus-circle me-2"/>New Request
          </button>
        </div>

        <div className="d-flex gap-2 mb-3 flex-wrap">
          {[['ALL','All',requests.length],['PENDING_L1','Pending L1',counts.PENDING_L1||0],['PENDING_L2','Pending L2',counts.PENDING_L2||0],['PENDING_L3','Pending L3 (HOD)',counts.PENDING_L3||0],['PENDING_FINANCE','Finance Queue',counts.PENDING_FINANCE||0],['FINANCE_APPROVED','Finance Approved',counts.FINANCE_APPROVED||0],['PAID','Paid',counts.PAID||0],['REJECTED','Rejected',counts.REJECTED||0]].map(([k,l,c])=>(
            <button key={k} className={`filter-chip ${filter===k?'active':'inactive'}`} onClick={()=>setFilter(k)}>{l} <span className="chip-count">{c}</span></button>
          ))}
        </div>

        <ListToolbar search={lc.search} setSearch={lc.setSearch} dateFrom={lc.dateFrom} setDateFrom={lc.setDateFrom} dateTo={lc.dateTo} setDateTo={lc.setDateTo} placeholder="Search by vendor, code, PO number..." dateLabel="Submitted"/>

        {loading?<div className="text-center py-5"><span className="spinner-border text-primary"/></div>
        :lc.paginated.length===0?<div className="empty-state"><i className="bi bi-clipboard-plus"/><p>No payment requests found</p></div>
        :lc.paginated.map((r,idx)=>{
          const sc=STATUS[r.status]||{cls:'status-pending',icon:'bi-circle',label:r.status};
          return (
            <div key={r._id} className="record-card">
              <div className="record-number">{(lc.page-1)*lc.pageSize+idx+1}.</div>
              <div className="record-body">
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <div style={{fontWeight:700,fontSize:'0.874rem',color:'#111827'}}>{r.vendorId?.name||'—'}</div>
                  <span className={`status-pill ${sc.cls}`}><i className={`bi ${sc.icon}`}/>{sc.label}</span>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-4">
                    <div className="record-field"><span className="rf-label">Request Code — </span><span className="rf-value text-primary">{r.paymentRequestCode}</span></div>
                    <div className="record-field"><span className="rf-label">Vendor Code — </span><span className="rf-value">{r.vendorId?.vendorCode||'—'}</span></div>
                    <div className="record-field"><span className="rf-label">Project — </span><span className="rf-value">{r.projectId?.projectCode||'—'}</span></div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="record-field"><span className="rf-label">PO Number — </span><span className="rf-value">{r.poNumber||'—'}</span></div>
                    <div className="record-field"><span className="rf-label">Cost Center — </span><span className="rf-value">{r.costCenter}</span></div>
                    <div className="record-field"><span className="rf-label">Payment Terms — </span><span className="rf-value">{r.paymentTerms}</span></div>
                    {r.poAttachment?.fileName&&(
                      <div className="record-field"><span className="rf-label">PO Attachment — </span><span className="rf-value"><i className="bi bi-paperclip me-1"/>{r.poAttachment.fileName}</span></div>
                    )}
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="record-field"><span className="rf-label">Amount — </span><span className="rf-value fw-bold text-primary">{money(r.requestedAmount)}</span></div>
                    {r.processedAmount&&<div className="record-field"><span className="rf-label">Processed — </span><span className="rf-value fw-bold text-success">{money(r.processedAmount)}</span></div>}
                    <div className="record-field"><span className="rf-label">Created — </span><span className="rf-value">{new Date(r.createdAt).toLocaleDateString('en-IN')}</span></div>
                  </div>
                </div>
                {r.approvalHistory?.length>0&&(
                  <div className="d-flex gap-1 mt-1 flex-wrap">
                    {r.approvalHistory.map((h,i)=>(
                      <span key={i} style={{fontSize:'0.598rem',padding:'1px 6px',borderRadius:20,background:h.action==='APPROVED'?'#f0fdf4':'#fef2f2',color:h.action==='APPROVED'?'#16a34a':'#dc2626',border:`1px solid ${h.action==='APPROVED'?'#bbf7d0':'#fecaca'}`}}>
                        {h.level}: {h.action}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <Pagination page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage} totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="payment requests"/>

        {modal&&(
          <div className="modal show d-block" style={{background:'rgba(0,0,0,0.45)'}}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header border-0"><h5 className="fw-bold"><i className="bi bi-clipboard-plus me-2 text-success"/>New Payment Request</h5><button className="btn-close" onClick={()=>setModal(false)}/></div>
                <div className="modal-body">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Vendor *</label>
                      <select className="form-select" value={form.vendorId} onChange={e=>set('vendorId',e.target.value)}>
                        <option value="">-- Select Vendor --</option>
                        {vendors.map(v=><option key={v._id} value={v._id}>{v.name} ({v.vendorCode})</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Project *</label>
                      <select className="form-select" value={form.projectId} onChange={e=>set('projectId',e.target.value)}>
                        <option value="">-- Select Project --</option>
                        {projects.map(p=><option key={p._id} value={p._id}>{p.projectCode} — {p.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4"><label className="form-label small fw-semibold">PO Number</label><input className="form-control" value={form.poNumber} onChange={e=>set('poNumber',e.target.value)} placeholder="PO-2025-001"/></div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Cost Center *</label>
                      <select className="form-select" value={form.costCenter} onChange={e=>set('costCenter',e.target.value)}>
                        {costCenters.map(cc=><option key={cc.code} value={cc.code}>{cc.code} — {cc.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Payment Terms</label>
                      <select className="form-select" value={form.paymentTerms} onChange={e=>set('paymentTerms',e.target.value)}>
                        {TERMS.map(t=><option key={t.code} value={t.code}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="col-12"><label className="form-label small fw-semibold">Material Details</label><textarea className="form-control" rows={2} value={form.materialDetails} onChange={e=>set('materialDetails',e.target.value)}/></div>
                    <div className="col-md-6"><label className="form-label small fw-semibold">Requested Amount (₹) *</label><input type="number" className="form-control" value={form.requestedAmount} onChange={e=>set('requestedAmount',e.target.value)}/></div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">PO Attachment</label>
                      <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e=>setPoFile(e.target.files?.[0]||null)}/>
                      {poFile&&<div className="small text-success mt-1"><i className="bi bi-paperclip me-1"/>{poFile.name}</div>}
                    </div>
                  </div>
                  <div className="mt-3 p-2 rounded" style={{background:'#fffbeb',border:'1px solid #fcd34d',fontSize:'0.718rem',color:'#92400e'}}>
                    <i className="bi bi-info-circle me-1"/>After submission, this will go through L1 (Reporting Manager) → L2 (Project Manager) → L3 (HOD) → Finance approval.
                  </div>
                </div>
                <div className="modal-footer border-0 gap-2">
                  <button className="btn btn-light rounded-3 px-4" onClick={()=>setModal(false)}>Cancel</button>
                  <button className="btn rounded-3 px-5 fw-semibold" style={{background:'linear-gradient(135deg,#1a5276,#1e8449)',color:'#fff',border:'none'}} onClick={handleSubmit} disabled={saving}>
                    {saving?<span className="spinner-border spinner-border-sm"/>:<><i className="bi bi-send me-1"/>Submit Request</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section></main></div>
    </>
  );
}
export default PaymentRequests;
